import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import confetti from 'canvas-confetti';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import Iconify from '@/components/iconify';
import { useBlackNovemberCampaign } from '@/hooks/useBlackNovemberCampaign';
import useResponsive from '@/hooks/useResponsive';
import {
  CAMPAIGN_PHASE,
  fillCampaignText,
  formatCampaignGoalShort,
  formatCampaignMoney,
} from '@/utils/blackNovemberCampaign';
import BlackNovemberAward from './BlackNovemberAward';
import BlackNovemberDialog from './BlackNovemberDialog';
import BlackNovemberMilestones from './BlackNovemberMilestones';
import { BLACK_NOVEMBER_COPY } from './blackNovemberCopy';
import { BLACK_CAKTO } from './blackNovemberTheme';

const CELEBRATED_KEY = 'blackNovember2026Celebrated';

const celebrateOnce = () => {
  let alreadyCelebrated = false;

  try {
    alreadyCelebrated = window.localStorage.getItem(CELEBRATED_KEY) === 'true';
    window.localStorage.setItem(CELEBRATED_KEY, 'true');
  } catch (error) {
    // Sem localStorage a festa repete a cada carregamento — não é motivo para
    // deixar de comemorar.
  }

  if (alreadyCelebrated) return;

  const colors = ['#4E9A66', '#C6E8D1', '#E4FFEC', '#F4F6F4'];
  const end = Date.now() + 2500;

  const frame = () => {
    confetti({
      particleCount: 4,
      angle: 60,
      spread: 58,
      origin: { x: 0 },
      colors,
      disableForReducedMotion: true,
    });
    confetti({
      particleCount: 4,
      angle: 120,
      spread: 58,
      origin: { x: 1 },
      colors,
      disableForReducedMotion: true,
    });

    if (Date.now() < end) requestAnimationFrame(frame);
  };

  frame();
};

/**
 * A faixa da campanha Black Cakto 100K, no vão do header entre a busca e os
 * ícones da conta, no visual da comunicação oficial (ver `blackNovemberTheme`).
 *
 * Quem decide a fase é a API — inclusive quando a campanha some do ar. Aqui só
 * se escolhe texto e realce, porque a estrutura (troféu, rótulo, valor, régua,
 * ação) é a mesma nas quatro aparências.
 *
 * No celular o vão do header sobra com ~80–150px e a faixa pede ~230–300px, então
 * ela muda de lugar em vez de espremer: o `DashboardLayout` monta a mesma faixa
 * com `placement="content"`, no topo da página. Cada instância se recolhe fora do seu
 * breakpoint, e as duas leem a mesma `queryKey`, então o request não duplica.
 */
export default function BlackNovemberStrip({ placement = 'header', dense = false }) {
  const { progress, isVisible, isCompact, isDismissible, dismiss } = useBlackNovemberCampaign();
  const isDesktop = useResponsive('up', 'md');
  const isPhone = useResponsive('down', 'sm');
  const [isDialogOpen, setDialogOpen] = useState(false);

  const isInContent = placement === 'content';
  const isShown = isVisible && isInContent === isPhone;

  const phase = progress?.phase;
  const isWon = phase === CAMPAIGN_PHASE.RESULTS_WON;

  useEffect(() => {
    if (isShown && isWon) celebrateOnce();
  }, [isShown, isWon]);

  if (!isShown || !progress) return null;

  const copy = BLACK_NOVEMBER_COPY.strip;
  const isTeaser = phase === CAMPAIGN_PHASE.TEASER;
  const isMissed = phase === CAMPAIGN_PHASE.RESULTS_MISSED;

  let content = copy.running;

  if (isTeaser) content = copy.teaser;
  else if (isWon) content = copy.won;
  else if (isMissed) content = copy.missed;
  else if (progress.achieved) content = copy.achieved;

  // O `isCompact` só vale no header, onde o alerta de cadastro pendente disputa a
  // largura do AppBar. No bloco do conteúdo não há disputa, e a régua é o que o
  // celular mais precisa ver.
  const showProgress = !isTeaser && (isInContent || (!isCompact && isDesktop));
  const isCelebrating = isWon || progress.achieved;
  const fill = (text) =>
    fillCampaignText(text, { year: progress.year, goal: progress.goal, sales: progress.sales });

  const headline = (() => {
    if (isTeaser) return { value: copy.teaser.headline };
    if (isWon || isMissed) return { value: `${formatCampaignMoney(progress.sales)} em novembro` };

    // Em tela estreita os centavos não cabem ao lado do troféu e da meta.
    return {
      value: formatCampaignMoney(progress.sales, { cents: isDesktop }),
      goal: `/ ${formatCampaignGoalShort(progress.goal)}`,
    };
  })();

  const aside = (() => {
    if (phase !== CAMPAIGN_PHASE.RUNNING || progress.achieved) {
      return <span>{fill(content.aside)}</span>;
    }

    return (
      <span>
        {copy.missing}{' '}
        <Box component="span" sx={{ color: BLACK_CAKTO.mint, fontWeight: 500 }}>
          {formatCampaignMoney(progress.missing)}
        </Box>
        {progress.daysLeft ? ` · ${progress.daysLeft} dias` : ''}
      </span>
    );
  })();

  // Baixa no header de 60–64 px, para sobrar respiro acima e abaixo.
  const headerPaddingY = dense ? 0.375 : 0.75;

  const openDialog = () => setDialogOpen(true);

  const handleDismiss = (event) => {
    event.stopPropagation();
    dismiss();
  };

  const labelRow = (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      <Typography
        noWrap
        sx={{
          fontSize: 10,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: isMissed ? BLACK_CAKTO.muted : BLACK_CAKTO.mintLine,
        }}
      >
        {BLACK_NOVEMBER_COPY.campaign}
      </Typography>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.375,
          flexShrink: 0,
          fontSize: 9.5,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          px: 0.875,
          py: '1px',
          borderRadius: 99,
          border: '1px solid',
          borderColor: isCelebrating ? BLACK_CAKTO.lineStrong : BLACK_CAKTO.line,
          color: isCelebrating ? BLACK_CAKTO.mint : BLACK_CAKTO.muted,
          whiteSpace: 'nowrap',
        }}
      >
        {isCelebrating ? <Iconify icon="eva:checkmark-fill" width={11} /> : null}
        {content.tag}
      </Box>
    </Stack>
  );

  const headlineText = (
    <Typography
      noWrap={isInContent}
      sx={{
        mt: 0.125,
        fontSize: 15,
        fontWeight: 400,
        lineHeight: 1.25,
        letterSpacing: '-0.01em',
        color: BLACK_CAKTO.ink,
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {headline.value}
      {headline.goal ? (
        <Box component="span" sx={{ color: BLACK_CAKTO.muted, fontSize: 13, ml: 0.5 }}>
          {headline.goal}
        </Box>
      ) : null}
    </Typography>
  );

  const milestones = (
    <BlackNovemberMilestones
      percent={progress.percent}
      goal={progress.goal}
      label={copy.progress}
      muted={isMissed}
    />
  );

  const asideText = (
    <Typography noWrap component="div" sx={{ minWidth: 0, fontSize: 12, color: BLACK_CAKTO.muted }}>
      {aside}
    </Typography>
  );

  const dismissButton = isDismissible ? (
    <Tooltip title={copy.dismiss}>
      <IconButton
        size="small"
        aria-label={copy.dismiss}
        onClick={handleDismiss}
        sx={{ flexShrink: 0, color: BLACK_CAKTO.faint, p: 0.5 }}
      >
        <Iconify icon="eva:close-fill" width={16} />
      </IconButton>
    </Tooltip>
  ) : null;

  const headerLayout = (
    <>
      <BlackNovemberAward height={dense ? 34 : 44} muted={isMissed} />

      <Box sx={{ minWidth: 0, flexShrink: 0 }}>
        {labelRow}
        {headlineText}
      </Box>

      {showProgress ? (
        <Box sx={{ flex: 1, minWidth: 120, px: 0.75 }}>{milestones}</Box>
      ) : (
        <Box sx={{ flex: 1 }} />
      )}

      {isDesktop ? asideText : null}

      {isDesktop ? (
        <Box
          component="span"
          sx={{
            flexShrink: 0,
            fontSize: 12.5,
            fontWeight: 500,
            px: 1.75,
            py: 0.875,
            borderRadius: 99,
            whiteSpace: 'nowrap',
            ...(isMissed
              ? { color: BLACK_CAKTO.ink, border: `1px solid ${BLACK_CAKTO.line}` }
              : { color: BLACK_CAKTO.onLight, background: BLACK_CAKTO.ink }),
          }}
        >
          {content.action}
        </Box>
      ) : null}

      {dismissButton}
    </>
  );

  const contentLayout = (
    <>
      <BlackNovemberAward height={52} muted={isMissed} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {labelRow}
        {headlineText}
        {showProgress ? <Box sx={{ mt: 1 }}>{milestones}</Box> : null}
        <Box sx={{ mt: 0.75 }}>{asideText}</Box>
      </Box>

      {dismissButton}
    </>
  );

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        aria-label={`${fill(BLACK_NOVEMBER_COPY.campaignWithYear)}: ${headline.value}`}
        onClick={openDialog}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          openDialog();
        }}
        sx={{
          // No conteúdo o respiro lateral é o mesmo do `Container` das páginas no
          // xs, pra a faixa alinhar com o que vem embaixo dela.
          ...(isInContent ? { mx: 2, mb: 2 } : { width: 1 }),
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 1.5,
          border: `1px solid ${BLACK_CAKTO.line}`,
          background: BLACK_CAKTO.surface,
          fontFamily: BLACK_CAKTO.font,
          '& .MuiTypography-root': { fontFamily: 'inherit' },
          cursor: 'pointer',
          transition: 'border-color .16s',
          '&:hover': { borderColor: BLACK_CAKTO.lineStrong },
          // O brilho verde atrás do troféu, como no palco do site.
          '&::before': {
            content: '""',
            position: 'absolute',
            left: -36,
            top: '50%',
            width: 150,
            height: 150,
            transform: 'translateY(-50%)',
            background: isMissed ? 'none' : BLACK_CAKTO.glow,
            pointerEvents: 'none',
          },
        }}
      >
        <Stack
          direction="row"
          alignItems={isInContent ? 'flex-start' : 'center'}
          spacing={1.5}
          sx={{
            position: 'relative',
            pl: 1,
            pr: isInContent ? 1.5 : 1.25,
            py: isInContent ? 1.25 : headerPaddingY,
            transition: 'padding .2s',
          }}
        >
          {isInContent ? contentLayout : headerLayout}
        </Stack>
      </Box>

      <BlackNovemberDialog
        open={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        progress={progress}
      />
    </>
  );
}

BlackNovemberStrip.propTypes = {
  placement: PropTypes.oneOf(['header', 'content']),
  dense: PropTypes.bool,
};
